/**
 * inventoryModes — registry of scoped "purpose-driven" engagements of
 * the Document Inventory tab.
 *
 * Same registry pattern as inventoryFilterAxes / inventoryBulkActions /
 * inventoryColumns. Adding a new mode is one entry here plus whatever
 * bulk-action entry the mode contributes (see inventoryBulkActions.js).
 *
 * A mode is activated when its `urlParam` is present in the inventory
 * URL. Modes are inert until activated.
 *
 * Each entry shape:
 *   key            — stable id (matches INVENTORY_MODES object key)
 *   urlParam       — `?<urlParam>=<id>` activates the mode
 *   label          — short label shown in the banner (e.g. "Finding a document")
 *   icon           — emoji prefix
 *   banner(ctx)    — ({ context }) => string. Renders the dynamic copy.
 *   bulkActions    — array of bulk-action keys that the mode contributes
 *                    or wants surfaced. The bulk-action registry still
 *                    gates each via its own isApplicable(docs, ctx).
 *   returnPath(ctx) — ({ context, caseId }) => path string. Where to
 *                    navigate on Cancel / completion.
 *   resolveContext  — async ({ caseId, params, sessionStorage }) =>
 *                    object | null. Reads URL params (+ optional stash)
 *                    into a richer context object the banner + actions
 *                    use. Called once when the mode activates. May
 *                    return null on failure → mode silently does not
 *                    activate.
 *
 * Phase 1 ships the primitive with NO registered modes — the registry
 * is intentionally empty, so any `?match_item=xxx` URL is a no-op.
 * Phase 2 adds the `matchChecklistItem` consumer.
 */

export const INVENTORY_MODES = {
  matchChecklistItem: {
    key: 'matchChecklistItem',
    urlParam: 'match_item',
    label: 'Finding a document for',
    icon: '📎',
    banner: ({ context }) =>
      `"${context.itemLabel || 'checklist item'}" — click 📎 Use as match on any document below.`,
    bulkActions: ['useAsChecklistMatch'],
    returnPath: ({ context, caseId }) =>
      `/case/${caseId}/checklist?match_item=${context.itemId}`,
    async resolveContext({ params, sessionStorage }) {
      // Mode lives in sessionStorage — no cross-reload persistence in v1
      // (per Inventory Mode plan § 4 non-goal). If a deep-linked URL hits
      // here without a stash, the mode silently doesn't activate and the
      // inventory tab renders normally.
      const itemId = params.get('match_item');
      if (!itemId || !sessionStorage) return null;
      const stashKey = `mode:matchChecklistItem:${itemId}`;
      try {
        const raw = sessionStorage.getItem(stashKey);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        sessionStorage.removeItem(stashKey);
        return {
          itemId,
          itemLabel: parsed.itemLabel || null,
          itemDescription: parsed.itemDescription || null,
        };
      } catch {
        return null;
      }
    },
  },
};

/**
 * Find the mode (if any) activated by the current URLSearchParams.
 * Returns the mode definition or null.
 */
export function findActiveMode(searchParams) {
  for (const mode of Object.values(INVENTORY_MODES)) {
    if (searchParams.get(mode.urlParam) != null) return mode;
  }
  return null;
}
